import { useEffect, useState } from "react";
import { UserPlus, LogOut, Car, IdCard } from "lucide-react";
import { http, formatApiError } from "../../lib/api";
import { ExpectedToday } from "./ExpectedToday";
import { SearchSelect } from "../SearchSelect";

const ipt = "w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white";
const hm = (iso) => new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
const EMPTY = { name: "", company: "", host: "", purpose: "", plate: "", badge_key_id: "" };

function ArrivalForm({ sites, keys, onDone }) {
  const expectedNote = <span className="block text-xs font-semibold text-amber-700">Visiteur absent de la liste : il sera noté « non annoncé »</span>;
  const [f, setF] = useState({ ...EMPTY, site_id: sites[0]?.id || "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const badges = keys.filter((k) => k.site_id === f.site_id && k.kind === "badge" && !k.loan);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { await http.post("/agent-app/visitors", { ...f, badge_key_id: f.badge_key_id || null }); setF({ ...EMPTY, site_id: f.site_id }); onDone(); }
    catch (er) { setErr(formatApiError(er)); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form onSubmit={submit} className="bg-white border border-slate-200 rounded-xl p-4 space-y-2" data-testid="visitor-form">
      <h2 className="font-display text-lg font-extrabold">Arrivée d&apos;un visiteur {expectedNote}</h2>
      {sites.length > 1 && <SearchSelect data-testid="visitor-site" value={f.site_id} onChange={(e) => setF({ ...f, site_id: e.target.value, badge_key_id: "" })} className={ipt}>{sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</SearchSelect>}
      <input data-testid="visitor-name" required value={f.name} onChange={set("name")} placeholder="Nom et prénom *" className={ipt} />
      <input data-testid="visitor-company" value={f.company} onChange={set("company")} placeholder="Société" className={ipt} />
      <div className="grid grid-cols-2 gap-2">
        <input data-testid="visitor-host" value={f.host} onChange={set("host")} placeholder="Personne visitée" className={ipt} />
        <input data-testid="visitor-plate" value={f.plate} onChange={set("plate")} placeholder="Véhicule (immat.)" className={`${ipt} uppercase`} />
      </div>
      <input data-testid="visitor-purpose" value={f.purpose} onChange={set("purpose")} placeholder="Motif de la visite" className={ipt} />
      <select data-testid="visitor-badge" value={f.badge_key_id} onChange={set("badge_key_id")} className={ipt}>
        <option value="">Aucun badge remis</option>
        {badges.map((k) => <option key={k.id} value={k.id}>Badge : {k.label}</option>)}
      </select>
      {err && <div data-testid="visitor-error" className="text-sm text-rose-700">{err}</div>}
      <button data-testid="visitor-submit" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-[#0B2A7A] text-white font-bold rounded-lg py-3 disabled:opacity-60"><UserPlus className="h-4 w-4" />Enregistrer l&apos;arrivée</button>
    </form>
  );
}

export function AgentVisitors() {
  const [items, setItems] = useState([]);
  const [sites, setSites] = useState([]);
  const [keys, setKeys] = useState([]);
  const [expected, setExpected] = useState([]);
  const [err, setErr] = useState("");
  const load = () => Promise.all([http.get("/agent-app/visitors"), http.get("/agent-app/keys"), http.get("/agent-app/expected-visitors")])
    .then(([v, k, x]) => { setItems(v.data); setKeys(k.data); setExpected(x.data); }).catch(() => {});
  useEffect(() => { load(); http.get("/agent-app/report-sites").then((r) => setSites(r.data)); }, []);
  const leave = async (v) => {
    if (!window.confirm(`Enregistrer le départ de ${v.name}${v.badge_label && !v.badge_returned_at ? ` et le retour du badge ${v.badge_label}` : ""} ?`)) return;
    try { await http.post(`/agent-app/visitors/${v.id}/leave`); load(); } catch (e) { setErr(formatApiError(e)); }
  };
  const present = items.filter((v) => !v.left_at);
  const gone = items.filter((v) => v.left_at);
  return (
    <div className="space-y-4" data-testid="agent-visitors">
      <ExpectedToday items={expected} keys={keys} onDone={load} />
      {sites.length > 0 && <ArrivalForm sites={sites} keys={keys} onDone={load} />}
      {err && <div className="text-sm text-rose-700">{err}</div>}
      <section>
        <h2 className="font-display text-lg font-extrabold mb-2">Présents sur site <span className="text-sm text-slate-500" data-testid="visitors-present-count">({present.length})</span></h2>
        <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100" data-testid="visitors-present">
          {present.length === 0 && <div className="p-5 text-center text-sm text-slate-500">Aucun visiteur présent.</div>}
          {present.map((v) => (
            <div key={v.id} className="px-4 py-3 flex items-center gap-3" data-testid={`visitor-${v.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm truncate">{v.name}{v.company && <span className="font-normal text-slate-500"> · {v.company}</span>}{v.announced === false && <span data-testid={`visitor-unannounced-${v.id}`} className="ml-1 text-[10px] font-bold uppercase rounded bg-amber-100 text-amber-800 px-1.5 py-0.5">Non annoncé</span>}</div>
                <div className="text-xs text-slate-500 truncate">Arrivé à {hm(v.arrived_at)}{v.host && ` · ${v.host}`}{v.purpose && ` · ${v.purpose}`}</div>
                <div className="text-xs text-slate-600 flex gap-3">{v.plate && <span className="inline-flex items-center gap-1"><Car className="h-3 w-3" />{v.plate}</span>}{v.badge_label && <span className="inline-flex items-center gap-1"><IdCard className="h-3 w-3" />{v.badge_label}</span>}</div>
              </div>
              <button data-testid={`visitor-leave-${v.id}`} onClick={() => leave(v)} className="inline-flex items-center gap-1 text-sm font-bold rounded-lg px-3 py-2 bg-emerald-600 text-white"><LogOut className="h-4 w-4" />Départ</button>
            </div>
          ))}
        </div>
      </section>
      {gone.length > 0 && (
        <section>
          <h2 className="font-display text-base font-extrabold mb-2 text-slate-600">Partis (24 h)</h2>
          <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100 text-sm" data-testid="visitors-gone">
            {gone.map((v) => <div key={v.id} className="px-4 py-2 text-slate-600">{v.name}{v.company && ` · ${v.company}`} — {hm(v.arrived_at)} → {hm(v.left_at)}</div>)}
          </div>
        </section>
      )}
    </div>
  );
}
